import {useEffect, useMemo, useState} from 'react';
import {getUserInfo} from './storage';
import {resolveUserNames} from './userNames';

/**
 * Read the signed-in user once for greeting headers (avatar, first name).
 *
 * Uses {@link module:helpers/storage.getUserInfo}, so demo mode's redaction
 * applies automatically. Fail-soft: while loading, or when nothing is stored,
 * the names are empty strings and callers fall back to a generic heading.
 *
 * @returns {{user: ?object, fullName: string, firstName: string, loaded: boolean}} The user and its names.
 */
export const useCurrentUser = () => {
    const [state, setState] = useState({user: null, loaded: false});

    useEffect(() => {
        let cancelled = false;

        getUserInfo()
            .then((user) => {
                if (!cancelled) {
                    setState({user: user || null, loaded: true});
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setState({user: null, loaded: true});
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return useMemo(() => {
        const {full, first} = resolveUserNames(state.user);

        return {user: state.user, fullName: full, firstName: first, loaded: state.loaded};
    }, [state]);
};

export default useCurrentUser;
